/**
 * @fileoverview Windows-style Taskbar
 * @description Bottom bar listing ALL open windows (route pages + dialogs).
 *
 * Features:
 * - Horizontal scroller with left/right scroll buttons + wheel-to-horizontal,
 *   so the bar can hold an unlimited number of windows without breaking layout.
 * - "Close all" action on the right side.
 * - Keyboard a11y: Tab into toolbar, Arrow keys move, Enter/Space activates,
 *   Delete/Esc closes focused window, Home/End jump.
 * - Closing a window from anywhere (in-window X, taskbar X, programmatic
 *   close) removes its chip immediately via the WindowManager unregister flow.
 */

import {
  startTransition,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useWindowManager } from '@/contexts/WindowManagerContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppWindow, ChevronLeft, ChevronRight, FileText, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export function MinimizedTaskbar() {
  const { windows, restore, minimize, unregister, requestClose, closeAll } = useWindowManager();
  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState({ left: false, right: false });

  const open = windows;
  const currentRouteKey = `${location.pathname}${location.search}`;

  const isActive = useCallback(
    (w: typeof windows[number]) => {
      if (w.kind === 'route') return !w.isMinimized && w.path === currentRouteKey;
      if (w.path && w.path !== currentRouteKey) return false;
      return !w.isMinimized;
    },
    [currentRouteKey],
  );

  const activateWindow = useCallback(
    (w: typeof windows[number]) => {
      const active = isActive(w);
      if (w.kind === 'route') {
        if (active) {
          minimize(w.id);
          return;
        }
        startTransition(() => {
          if (w.isMinimized) restore(w.id);
          if (w.path) navigate(w.path);
        });
        return;
      }
      if (w.path && w.path !== currentRouteKey) {
        startTransition(() => {
          navigate(w.path!);
          setTimeout(() => restore(w.id), 0);
        });
        return;
      }
      if (active) minimize(w.id);
      else restore(w.id);
    },
    [isActive, minimize, restore, navigate, currentRouteKey],
  );

  const closeWindow = useCallback(
    (w: typeof windows[number]) => {
      if (w.kind === 'route' && w.path === currentRouteKey) {
        navigate('/dashboard');
        setTimeout(() => unregister(w.id), 0);
        return;
      }
      if (w.kind === 'dialog') {
        requestClose(w.id);
        return;
      }
      unregister(w.id);
    },
    [unregister, requestClose, currentRouteKey, navigate],
  );

  // ---- Scroll affordances --------------------------------------------------
  const updateScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const left = el.scrollLeft > 4;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
    setScrollState((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  }, []);

  useLayoutEffect(() => {
    updateScrollState();
  }, [open.length, updateScrollState]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => updateScrollState();
    el.addEventListener('scroll', onScroll, { passive: true });
    const ro = new ResizeObserver(onScroll);
    ro.observe(el);
    window.addEventListener('resize', onScroll);
    return () => {
      el.removeEventListener('scroll', onScroll);
      ro.disconnect();
      window.removeEventListener('resize', onScroll);
    };
  }, [updateScrollState]);

  const scrollBy = (dx: number) => {
    scrollerRef.current?.scrollBy({ left: dx, behavior: 'smooth' });
  };

  // Wheel → horizontal scroll inside the chips lane.
  const onWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    const el = scrollerRef.current;
    if (!el) return;
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      el.scrollLeft += e.deltaY;
    }
  };

  // ---- Roving tabindex ----------------------------------------------------
  const focusChipAt = useCallback((index: number) => {
    const root = scrollerRef.current;
    if (!root) return;
    const chips = root.querySelectorAll<HTMLButtonElement>('[data-taskbar-chip]');
    if (chips.length === 0) return;
    const i = (index + chips.length) % chips.length;
    chips[i]?.focus();
    chips[i]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const chip = target.closest<HTMLButtonElement>('[data-taskbar-chip]');
    if (!chip) return;
    const root = scrollerRef.current;
    if (!root) return;
    const chips = Array.from(root.querySelectorAll<HTMLButtonElement>('[data-taskbar-chip]'));
    const idx = chips.indexOf(chip);
    const id = chip.dataset.windowId;
    const w = id ? open.find((x) => x.id === id) : undefined;

    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        focusChipAt(idx + 1);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        focusChipAt(idx - 1);
        break;
      case 'Home':
        e.preventDefault();
        focusChipAt(0);
        break;
      case 'End':
        e.preventDefault();
        focusChipAt(chips.length - 1);
        break;
      case 'Delete':
      case 'Escape':
        if (w) {
          e.preventDefault();
          const nextIdx = Math.min(idx, chips.length - 2);
          closeWindow(w);
          requestAnimationFrame(() => focusChipAt(Math.max(0, nextIdx)));
        }
        break;
    }
  };

  useEffect(() => {
    const root = scrollerRef.current;
    if (!root) return;
    const chips = root.querySelectorAll<HTMLButtonElement>('[data-taskbar-chip]');
    if (chips.length === 0) return;
    const hasFocused = Array.from(chips).some((c) => c.tabIndex === 0);
    if (!hasFocused) {
      chips.forEach((c, i) => {
        c.tabIndex = i === 0 ? 0 : -1;
      });
    }
  }, [open.length]);

  if (open.length === 0) return null;

  const activeIdx = open.findIndex(isActive);
  const tabbableIdx = activeIdx >= 0 ? activeIdx : 0;

  const handleCloseAll = () => {
    // If the user is on a route that has a chip, leave it first so the
    // RouteWindowHost doesn't immediately re-register it.
    const onRoute = open.some((w) => w.kind === 'route' && w.path === currentRouteKey);
    if (onRoute) navigate('/dashboard');
    setTimeout(() => closeAll(), 0);
  };

  return createPortal(
    <div
      ref={containerRef}
      data-window-taskbar
      className="fixed bottom-0 left-0 right-0 z-[60] border-t border-border/60 bg-gradient-to-b from-card/95 to-card/90 backdrop-blur-xl shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.18)]"
      role="toolbar"
      aria-label={`Open windows taskbar, ${open.length} window${open.length > 1 ? 's' : ''}`}
      aria-orientation="horizontal"
      onKeyDown={handleKeyDown}
    >
      <div className="flex items-stretch h-11">
        {/* Leading badge */}
        <div
          className="flex items-center gap-1.5 px-3 border-r border-border/60 text-xs font-semibold text-muted-foreground shrink-0"
          aria-hidden="true"
        >
          <AppWindow className="h-3.5 w-3.5 text-primary" />
          <span className="tabular-nums">{open.length}</span>
        </div>

        {/* Left scroll button */}
        <button
          type="button"
          tabIndex={-1}
          onClick={() => scrollBy(-240)}
          disabled={!scrollState.left}
          aria-label="Scroll windows left"
          className={cn(
            'shrink-0 w-7 flex items-center justify-center border-r border-border/60 transition-opacity',
            scrollState.left ? 'opacity-100 hover:bg-muted text-foreground' : 'opacity-30 cursor-default text-muted-foreground',
          )}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        {/* Chips lane */}
        <div
          ref={scrollerRef}
          onWheel={onWheel}
          className="flex-1 flex items-center gap-1.5 px-2 overflow-x-auto scrollbar-thin scroll-smooth [scrollbar-width:thin]"
          style={{ scrollbarWidth: 'thin' }}
        >
          {open.map((w, i) => {
            const active = isActive(w);
            const isTabbable = i === tabbableIdx;
            return (
              <div
                key={w.id}
                className={cn(
                  'group inline-flex items-stretch h-7 rounded-md text-xs shrink-0 border overflow-hidden transition-all',
                  active
                    ? 'bg-primary text-primary-foreground border-primary shadow-sm shadow-primary/20 ring-1 ring-primary/30'
                    : w.isMinimized
                      ? 'bg-muted/50 hover:bg-muted text-foreground border-border/40 hover:border-border/70'
                      : 'bg-card hover:bg-muted text-foreground border-border/60',
                )}
              >
                {/* Active accent strip */}
                {active && <span aria-hidden className="w-0.5 bg-primary-foreground/70" />}
                <button
                  type="button"
                  data-taskbar-chip
                  data-window-id={w.id}
                  tabIndex={isTabbable ? 0 : -1}
                  onClick={() => activateWindow(w)}
                  className="inline-flex items-center gap-1.5 pl-2 pr-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                  title={active ? `Minimize: ${w.title}` : `Switch to: ${w.title}`}
                  aria-pressed={active}
                  aria-label={`${active ? 'Minimize' : 'Switch to'} ${w.title}${w.subtitle ? ` — ${w.subtitle}` : ''}${w.isMinimized ? ' (minimized)' : ''}. Press Delete to close.`}
                >
                  <span aria-hidden="true" className={cn('shrink-0', active ? 'opacity-90' : 'text-primary')}>
                    {w.icon ??
                      (w.kind === 'route' ? (
                        <FileText className="h-3.5 w-3.5" />
                      ) : (
                        <AppWindow className="h-3.5 w-3.5" />
                      ))}
                  </span>
                  <span className="font-medium truncate max-w-[160px]">{w.title}</span>
                  {w.subtitle && (
                    <span
                      className={cn(
                        'truncate max-w-[100px]',
                        active ? 'text-primary-foreground/75' : 'text-muted-foreground',
                      )}
                    >
                      · {w.subtitle}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={(e) => {
                    e.stopPropagation();
                    closeWindow(w);
                  }}
                  className={cn(
                    'w-6 inline-flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                    active
                      ? 'hover:bg-primary-foreground/20'
                      : 'opacity-60 group-hover:opacity-100 hover:bg-destructive hover:text-destructive-foreground',
                  )}
                  aria-label={`Close ${w.title}`}
                  title="Close"
                >
                  <X className="h-3 w-3" aria-hidden="true" />
                </button>
              </div>
            );
          })}
        </div>

        {/* Right scroll button */}
        <button
          type="button"
          tabIndex={-1}
          onClick={() => scrollBy(240)}
          disabled={!scrollState.right}
          aria-label="Scroll windows right"
          className={cn(
            'shrink-0 w-7 flex items-center justify-center border-l border-border/60 transition-opacity',
            scrollState.right ? 'opacity-100 hover:bg-muted text-foreground' : 'opacity-30 cursor-default text-muted-foreground',
          )}
        >
          <ChevronRight className="h-4 w-4" />
        </button>

        {/* Close all */}
        <button
          type="button"
          onClick={handleCloseAll}
          className="shrink-0 px-3 inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground border-l border-border/60 hover:bg-destructive hover:text-destructive-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
          title="Close all windows"
          aria-label={`Close all ${open.length} windows`}
        >
          <X className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Close all</span>
        </button>
      </div>
    </div>,
    document.body,
  );
}
